<template>
  <div class="flex h-screen bg-gray-100 dark:bg-gray-900">

    <div class="flex-1 flex flex-col bg-white dark:bg-gray-800 shadow-lg m-2 rounded-lg overflow-hidden">
      <div class="p-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 flex justify-between items-center">
        <div class="flex items-center">
          <h2 class="text-lg font-semibold text-gray-800 dark:text-gray-200 mr-4">{{ t('applicationAgentView.liveApplicationPreview') }}</h2>
          <div v-if="detectedErrors !== null" class="tooltip tooltip-bottom" data-tip="{{ t('applicationAgentView.attemptToFixDetectedErrors') }}">
              <button @click="showErrorsModal = true" class="btn btn-xs btn-error btn-outline mr-4">
                  {{ t('applicationAgentView.fixErrors') }}
              </button>
          </div>
          <div class="form-control" v-if="currentHtml !== '' && currentHtml.includes('Error loading') === false">
            <label class="label cursor-pointer p-0">
              <span class="label-text text-sm mr-2 dark:text-gray-300">{{ t('applicationAgentView.manualEdit') }}</span> 
              <input type="checkbox" class="toggle toggle-sm toggle-primary" :checked="isEditMode === true" @change="toggleEditMode" />
            </label>
          </div>
          <button class="btn btn-sm btn-ghost btn-square ml-4 hidden" @click="showDbManagerModal = true" title="{{ t('applicationAgentView.manageDatabase') }}">
            <Icon icon="tabler:database" class="w-5 h-5" />
          </button>
        </div>
         <button 
              @click="openFullscreenPreview"
              class="btn btn-sm btn-ghost btn-square" 
              title="{{ t('applicationAgentView.openPreviewInNewTab') }}"
              :disabled="isLoadingApp === true || currentHtml === '' || currentHtml.includes('Error loading') === true || isEditMode === true"
              >
             <Icon icon="tabler:external-link" class="w-5 h-5" />
          </button>
      </div>
      
      <div class="flex-1 relative">
        <template v-if="isEditMode === false">
          <iframe 
            ref="previewFrame"
            :srcdoc="currentHtml"
            class="w-full h-full border-none"
            sandbox="allow-scripts allow-same-origin allow-forms"
            title="Application Preview"
          ></iframe>
          <div v-if="isAutoFixing === true" class="absolute inset-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm flex flex-col items-center justify-center z-20">
              <span class="loading loading-spinner loading-lg text-primary"></span>
              <p class="mt-4 text-lg font-semibold text-gray-700 dark:text-gray-200">{{ t('applicationAgentView.automaticallyFixingErrors') }}</p>
          </div>
        </template>
        <template v-else>
          <div class="flex-1 flex flex-col p-1 bg-gray-200 dark:bg-gray-900">
            <div 
              ref="monacoContainerRef" 
              class="flex-grow border border-gray-300 dark:border-gray-700 rounded overflow-hidden"
              style="min-height: 300px;" 
            ></div>
            <button 
              @click="saveManualEdit"
              class="btn btn-sm mt-1"
              :disabled="isSubmittingManualEdit === true || editedCode === currentHtml"
            >
              <span v-if="isSubmittingManualEdit === true" class="loading loading-spinner loading-xs"></span>
              <p class="text-white">{{ t('applicationAgentView.saveManualEdit') }}</p>
            </button>
          </div>
        </template>
      </div>
    </div>

    <div class="w-1/3 flex flex-col bg-white dark:bg-gray-800 shadow-lg m-2 ml-0 rounded-lg">
      <div class="p-3 border-b border-gray-200 dark:border-gray-600 flex justify-between items-center">
        <h3 class="text-lg font-semibold text-gray-800 dark:text-gray-200">{{ t('applicationAgentView.iterationHistory') }}</h3>
      </div>
      <div class="flex-1 overflow-y-auto p-3 space-y-2">
        <div v-if="iterations.length === 0 && isLoadingIterations === false" class="text-center text-gray-500 dark:text-gray-400 py-4">{{ t('applicationAgentView.noIterationsYet') }}</div>
         <div v-else-if="isLoadingIterations === true" class="text-center py-4">
             <span class="loading loading-dots loading-md"></span>
        </div>
        <button 
            v-for="(iteration, index) in iterations" 
            :key="iteration.iterationId" 
            @click="loadIteration(iteration.version)"
            class="btn btn-sm btn-block btn-ghost justify-start text-left text-gray-700 dark:text-white group"
            :class="{ 'btn-active': selectedVersion === iteration.version }"
            :title="iteration.iterationPrompt"
          >
           <span class="truncate flex-1">{{ iteration.iterationPrompt }}</span>
           <span class="text-xs text-gray-400 ml-2">{{ t('applicationAgentView.version') }}v{{ iteration.version }}</span>
            <span @click.stop="confirmDelete(iteration.version)" class="btn btn-xs btn-ghost btn-square text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity">
               <Icon icon="tabler:trash" class="w-4 h-4" />
            </span>
        </button>
      </div>


      <div class="p-3 border-t border-gray-200 dark:border-gray-600" :class="{'opacity-50 pointer-events-none': isEditMode === true}">
        <h3 class="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">{{ t('applicationAgentView.nextIterationPrompt') }}</h3>
        <textarea 
          v-model="newPrompt"
          class="textarea textarea-bordered w-full" 
          :placeholder="t('applicationAgentView.describeTheNextChange')"
          rows="4"
          :disabled="isSubmitting === true || isEditMode === true"
        ></textarea>
        <button 
          @click="submitNewPrompt"
          class="btn w-full mt-2" style="color:#fff !important"
          :disabled="isSubmitting === true || newPrompt.trim() === '' || isEditMode === true"
        >
          <span v-if="isSubmitting === true" class="loading loading-spinner loading-sm text-white" style="color:#fff !important"></span>
          {{ t('applicationAgentView.generateNextVersion') }}
        </button>
      </div>
    </div>
    
    <dialog :open="showDbManagerModal === true" class="modal">
        <div class="modal-box w-11/12 max-w-5xl">
            <div class="flex justify-between items-center pb-2 border-b">
              <h3 class="font-bold text-lg">{{ t('applicationAgentView.applicationDatabaseManager') }}</h3>
              <div class="form-control hidden">
                  <label class="label cursor-pointer">
                      <span class="label-text mr-2">{{ t('applicationAgentView.allowTableEdit') }}</span> 
                      <input type="checkbox" class="toggle toggle-sm" v-model="allowTableEdit" />
                  </label>
              </div>
            </div>
            <button class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2" @click="showDbManagerModal = false">✕</button>
            <div class="py-4">
                <ApplicationDatabaseManager :application-id="appId" :schema="dbSchema" @schema-updated="fetchDbSchema(appId)" :allow-table-edit="allowTableEdit"/>
            </div>
        </div>
        <form method="dialog" class="modal-backdrop">
            <button @click="showDbManagerModal = false">{{ t('applicationAgentView.close') }}</button>
        </form>
    </dialog>

    <dialog :open="showErrorsModal === true" class="modal">
        <div class="modal-box max-w-3xl">
            <div class="border-b dark:border-gray-600 pb-2 flex justify-between items-center">
                <h3 class="font-bold text-lg text-red-500">{{ t('applicationAgentView.errorsDetectedInCode') }}</h3>
                <button class="btn btn-sm btn-circle btn-ghost" @click="showErrorsModal = false">✕</button>
            </div>
             <div class="py-4 space-y-4">
                <p>{{ t('applicationAgentView.aiGeneratedCodeContainsErrors') }}</p>
                <div class="font-mono text-sm bg-gray-100 dark:bg-gray-700 p-3 rounded-lg max-h-60 overflow-y-auto">
                    <p v-for="(error, i) in detectedErrors" :key="i" class="whitespace-pre-wrap text-red-500">{{ error }}</p>
                </div>
            </div>
            <div class="modal-action">
                <button class="btn btn-ghost" @click="showErrorsModal = false">{{ t('applicationAgentView.cancel') }}</button>
                <button class="btn" @click="sendErrorsToAI" :disabled="isSubmitting != false">{{ t('applicationAgentView.attemptFix') }}</button>
            </div>
        </div>
         <form method="dialog" class="modal-backdrop">
            <button @click="showErrorsModal = false">{{ t('applicationAgentView.close') }}</button>
        </form>
    </dialog>

    <dialog :open="showDeleteConfirmModal === true" class="modal">
        <div class="modal-box">
            <h3 class="font-bold text-lg">{{ t('applicationAgentView.confirmDeletion') }}</h3>
            <p class="py-4">{{ t('applicationAgentView.areYouSureYouWantToDeleteVersion') }} {{ versionToDelete }}?  {{ t('applicationAgentView.thisActionCannotBeUndone') }}</p>
            <div class="modal-action">
                <button class="btn btn-ghost" @click="showDeleteConfirmModal = false">{{ t('applicationAgentView.cancel') }}</button>
                <button class="btn btn-error" @click="executeDelete"><p class="text-white">{{ t('applicationAgentView.delete') }}</p></button>
            </div>
        </div>
        <form method="dialog" class="modal-backdrop">
            <button @click="showDeleteConfirmModal = false">{{ t('applicationAgentView.close') }}</button>
        </form>
    </dialog>
  </div>
</template>

<script setup lang="js">
import { ref, onMounted, computed, watch, nextTick, onUnmounted } from 'vue';
import axios from '@/api/axiosInstance';
import { useToast } from 'vue-toastification';
import { globalState } from '@/globalState';
import { useRouter, useRoute } from 'vue-router';
import { Icon } from '@iconify/vue/dist/offline';
import loader from '@monaco-editor/loader';
import ApplicationDatabaseManager from '@/components/ApplicationDatabaseManager.vue';
import { useI18n } from '@/i18n';
import { isDefinedAndNotEmpty } from '../../isDefinedAndNotEmpty.js';
const { t } = useI18n();

const toast = useToast();
const router = useRouter();
const route = useRoute();

const appId = ref(route.params.appId);
const appDetails = ref(null);
const currentHtml = ref('<html><head><title>Loading...</title></head><body><h1>Loading application preview...</h1></body></html>');
const iterations = ref([]);
const newPrompt = ref('');
const isLoadingApp = ref(false);
const isLoadingIterations = ref(false);
const isSubmitting = ref(false);
const selectedVersion = ref(null);
const previewFrame = ref(null); 
const isEditMode = ref(false);
const editedCode = ref('');
const isSubmittingManualEdit = ref(false);

const showDbManagerModal = ref(false);
const dbSchema = ref([]);
const detectedErrors = ref(null);
const showErrorsModal = ref(false);
const isAutoFixing = ref(false);
const showDeleteConfirmModal = ref(false);
const versionToDelete = ref(null);
const allowTableEdit = ref(false);

const monacoContainerRef = ref(null);
let monacoEditorInstance = null; 
const isMonacoLoading = ref(false);

function getSafeScriptJson(value) {
    const raw = value !== undefined && value !== null ? String(value) : '';
    return JSON.stringify(raw).replace(/</g, '\\u003C').replace(/>/g, '\\u003E').replace(/&/g, '\\u0026');
}

function getAuthToken() {
  const token = document.cookie.split('; ').find(row => row.startsWith('authToken=')!=false)?.split('=')[1];
  if (token === undefined || token === null) {
    return '';
  }
  return token;
}

const processGeneratedHtml = (html) => {
    if (html === undefined || html === null || typeof html !== 'string') {
        return html;
    }

    let processedHtml = html;

    const messageTargetOrigin = getSafeScriptJson(window.location.origin);
    const errorListenerScript = `<script>
        window.onerror = function(message, source, lineno, colno, error) {
            window.parent.postMessage({
                type: 'iframeError',
                message: message,
                source: source,
                lineno: lineno,
                colno: colno,
                error: error ? error.stack : null
            }, ${messageTargetOrigin});
            return true;
        };
    <` + `/script>`;
    
    const headTag = '<head>';
    if (processedHtml.toLowerCase().includes(headTag) === true) {
        const headIndex = processedHtml.toLowerCase().indexOf(headTag) + headTag.length;
        processedHtml = processedHtml.slice(0, headIndex) + errorListenerScript + processedHtml.slice(headIndex);
    } else {
        processedHtml = errorListenerScript + processedHtml;
    }

    const endpointInjection = `<script>window.backendEndpoint = ${getSafeScriptJson(globalState.apiEndpoint)};<` + `/script>`;
    if (processedHtml.toLowerCase().includes(headTag) === true) {
        const headIndex = processedHtml.toLowerCase().indexOf(headTag) + headTag.length;
        processedHtml = processedHtml.slice(0, headIndex) + endpointInjection + processedHtml.slice(headIndex);
    } else {
        processedHtml = endpointInjection + processedHtml;
    }

    if (appId.value !== undefined && appId.value !== null) {
        const injectionScript = `<script>window.applicationID = ${getSafeScriptJson(appId.value)};<` + `/script>`;
        const bodyCloseTag = '</' + 'body>';
        if (processedHtml.toLowerCase().includes(bodyCloseTag) === true) {
            const bodyEndIndex = processedHtml.toLowerCase().lastIndexOf(bodyCloseTag);
            processedHtml = processedHtml.slice(0, bodyEndIndex) + injectionScript + processedHtml.slice(bodyEndIndex);
        } else {
            processedHtml = processedHtml + injectionScript;
        }
    }
    
    return processedHtml;
};

const getAuthHeaders = () => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json'
  };
  if (token !== '' && token !== undefined && token !== null) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}


const fetchAppData = async (id = appId.value) => {
  isLoadingApp.value = true;
  try {
    const response = await axios.get(`${globalState.apiEndpoint}/api/applications/get/${id}`, {
      headers: getAuthHeaders()
    });
    appDetails.value = response.data;
    currentHtml.value = processGeneratedHtml( isDefinedAndNotEmpty(response.data.pageCode) != false ? response.data.pageCode : '<p>No code generated yet.</p>'); 

    selectedVersion.value = null;
    console.log("Fetched latest app data:", appDetails.value);
  } catch (error) {
    console.error('Error fetching application data:', error);
    toast.error(isDefinedAndNotEmpty(error.response?.data?.error) != false ? error.response?.data?.error : 'Failed to fetch application data.');
    currentHtml.value = '<p style="color: red;">Error loading application data.</p>';
  } finally {
    isLoadingApp.value = false;
  }
};

const fetchDbSchema = async (id = appId.value) => {
    try {
        const response = await axios.get(`${globalState.apiEndpoint}/api/appDB/${id}/schema`, {
            headers: getAuthHeaders()
        });
        dbSchema.value = response.data;
    } catch (error) {
        console.error('Error fetching DB schema:', error);
        toast.error(isDefinedAndNotEmpty(error.response?.data?.error) != false ? error.response?.data?.error : 'Failed to fetch DB schema.');
    }
};

const fetchIterations = async (id = appId.value) => {
  isLoadingIterations.value = true;
  try {
    const response = await axios.get(`${globalState.apiEndpoint}/api/applications/iterations/${id}`, {
      headers: getAuthHeaders()
    });
    iterations.value = response.data;
    console.log("Fetched iterations:", iterations.value);
  } catch (error) {
    console.error('Error fetching iterations:', error);
    toast.error(isDefinedAndNotEmpty(error.response?.data?.error) != false ? error.response?.data?.error : 'Failed to fetch iteration history.');
  } finally {
    isLoadingIterations.value = false;
  }
};

const loadIteration = async (version) => {
  isLoadingApp.value = true; 
  selectedVersion.value = version;
  try {
    const response = await axios.get(`${globalState.apiEndpoint}/api/applications/get/${appId.value}?version=${version}`, {
      headers: getAuthHeaders()
    });
    currentHtml.value = processGeneratedHtml(response.data.generatedCode || '<p>Error loading iteration code.</p>');
    console.log(`Loaded version ${version} code.`);
  } catch (error) {
    console.error(`Error fetching version ${version}:`, error);
    toast.error(isDefinedAndNotEmpty(error.response?.data?.error) != false ? error.response?.data?.error : `Failed to fetch version ${version}.`);
    currentHtml.value = `<p style="color: red;">Error loading version ${version}.</p>`;
  } finally {
    isLoadingApp.value = false;
  }
};

const submitNewPrompt = async () => {
  if (newPrompt.value.trim() === '' || isSubmitting.value === true) return;

  isSubmitting.value = true;
  selectedVersion.value = null; 
  try {
    const payload = {
      appId: appId.value,
      iterationPrompt: newPrompt.value
    };
    const response = await axios.post(`${globalState.apiEndpoint}/api/applications/query`, payload, {
      headers: getAuthHeaders()
    });
    currentHtml.value = processGeneratedHtml(isDefinedAndNotEmpty(response.data.htmlContent) != false ? response.data.htmlContent : '<p>Error: No content received.</p>');
    newPrompt.value = ''; 
    toast.success('New version generated!');
    await fetchIterations(); 
  } catch (error) {
    console.error('Error submitting new prompt:', error);
    toast.error(isDefinedAndNotEmpty(error.response?.data?.error) != false ? error.response?.data?.error : 'Failed to generate next version.');
  } finally {
    isSubmitting.value = false;
  }
};

const handleIframeMessage = (event) => {
    if (isAutoFixing.value === true) {
        return;
    }
    const expectedSource = previewFrame.value !== null && previewFrame.value !== undefined ? previewFrame.value.contentWindow : null;
    if (expectedSource === null || event.source !== expectedSource) {
        return;
    }
    if (event.origin !== window.location.origin) {
        return;
    }

    if (event.data !== null && event.data !== undefined && event.data.type === 'iframeError') {
        const errorMessage = `[Line ${event.data.lineno}]: ${event.data.message}`;
        console.warn('Error received from iframe:', errorMessage);
        
        if (detectedErrors.value !== null && detectedErrors.value.includes(errorMessage) === true) {
            return;
        }

        if (detectedErrors.value === null) {
            detectedErrors.value = [];
        }
        detectedErrors.value.push(errorMessage);
        
        sendErrorsToAI(true);
    }
};

const sendErrorsToAI = async (isAuto = false) => {
    if (detectedErrors.value === null || detectedErrors.value.length === 0) {
        toast.info("No errors to send.");
        showErrorsModal.value = false;
        return;
    }
  
    const errorPrompts = detectedErrors.value.map(e => `Error: "${e}"`);
    newPrompt.value = `The following errors were detected in the previous code. Please fix them.\n${errorPrompts.join('\n')}`;
  
    showErrorsModal.value = false;
    detectedErrors.value = null;

    if (isAuto === true) {
        isAutoFixing.value = true;
    }

    try {
      await submitNewPrompt();
    } finally {
       if (isAuto === true) {
        isAutoFixing.value = false;
      }
    }
};

const openFullscreenPreview = () => {
  const safeAppId = encodeURIComponent(String(appId.value));
  let url = `/app/${safeAppId}/fullscreen`;
  if (selectedVersion.value !== undefined && selectedVersion.value !== null) {
    url += `?version=${encodeURIComponent(String(selectedVersion.value))}`;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
};

const confirmDelete = (version) => {
    versionToDelete.value = version;
    showDeleteConfirmModal.value = true;
};

const executeDelete = () => {
    if (versionToDelete.value !== null) {
        deleteIteration(versionToDelete.value);
    }
    showDeleteConfirmModal.value = false;
    versionToDelete.value = null;
};

const deleteIteration = async (version) => {
    try {
        const response = await axios.delete(`${globalState.apiEndpoint}/api/applications/iterations/${appId.value}/${version}`, {
            headers: getAuthHeaders()
        });
        toast.success(isDefinedAndNotEmpty(response.data.message) != false ? response.data.message : 'Iteration deleted.');
        await fetchIterations();
        if (selectedVersion.value === version) {
            selectedVersion.value = null;
            await fetchAppData();
        }
    } catch (error) {
        console.error(`Error deleting version ${version}:`, error);
        toast.error(isDefinedAndNotEmpty(error.response?.data?.error) != false ? error.response?.data?.error : 'Failed to delete iteration.');
    }
};

const initMonaco = async () => {
  if (monacoContainerRef.value !== null && monacoEditorInstance === null && isMonacoLoading.value === false) {
    isMonacoLoading.value = true;
    try {
      const monaco = await loader.init();
      monacoEditorInstance = monaco.editor.create(monacoContainerRef.value, {
        value: editedCode.value, 
        language: 'html',
        theme: globalState.isDarkMode === true ? 'vs-dark' : 'vs',
        automaticLayout: true,
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
        wordWrap: 'on',
      });

      monacoEditorInstance.onDidChangeModelContent(() => {
        if (monacoEditorInstance !== null) {
          editedCode.value = monacoEditorInstance.getValue();
        }
      });
      console.log("Monaco editor initialized for ApplicationAgentView.");
    } catch (error) {
      console.error("Failed to initialize Monaco editor:", error);
      toast.error("Could not load code editor.");
  
    } finally {
      isMonacoLoading.value = false;
    }
  }
};

const destroyMonaco = () => {
  if (monacoEditorInstance !== null) {
    monacoEditorInstance.dispose();
    monacoEditorInstance = null;
    console.log("Monaco editor disposed for ApplicationAgentView.");
  }
};

const toggleEditMode = async () => {
  isEditMode.value = isEditMode.value === false;
  if (isEditMode.value === true) {
    editedCode.value = currentHtml.value;
    await nextTick(); 
    initMonaco();
  } else {
    destroyMonaco();
  }
};

watch(() => globalState.isDarkMode, (newValue) => {
  if (monacoEditorInstance !== null) {
    monacoEditorInstance.updateOptions({ theme: newValue === true ? 'vs-dark' : 'vs' });
  }
});

const saveManualEdit = async () => {
  if (editedCode.value === currentHtml.value) {
    toast.info("No changes to save.");
    return;
  }
  isSubmittingManualEdit.value = true;

  try {
    const payload = {
      appId: appId.value,
      htmlContent: editedCode.value,
      iterationPrompt: `Manual Edit - ${new Date().toLocaleTimeString()}`
    };
    const response = await axios.post(`${globalState.apiEndpoint}/api/applications/manualEdit`, payload, {
      headers: getAuthHeaders()
    });
    currentHtml.value = processGeneratedHtml(editedCode.value);
    toast.success("Manual edit saved.");
    await fetchIterations();
    isEditMode.value = false;
  } catch (error) {
    console.error('Error saving manual edit:', error);
    toast.error(isDefinedAndNotEmpty(error.response?.data?.error) != false ? error.response?.data?.error : 'Failed to save manual edit.');
  } finally {
    isSubmittingManualEdit.value = false;
  }
};

onMounted(() => {
  window.addEventListener('message', handleIframeMessage);
  if (appId.value !== undefined && appId.value !== null) {
    fetchAppData(appId.value);
    fetchIterations(appId.value);
    fetchDbSchema(appId.value);
  } else {
    toast.error("Application ID is missing from the URL.");
    currentHtml.value = "Error: No Application ID provided.";
  }
});

onUnmounted(() => {
  destroyMonaco();
  window.removeEventListener('message', handleIframeMessage);
});

</script>

<style scoped>

.flex-1 {
  flex: 1 1 0%;
}
.h-screen {
    height: calc(100vh - 64px); 
}

.btn-block {
    white-space: normal; 
    height: auto; 
    min-height: 2.5rem; 
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
}
.btn-block .truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style> 